import { useQuery } from 'react-query';

import { getRingCentralCommunications } from 'apis/RingCentralAPI';
import { canViewRingCentralRecords } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { PaginatedResponseType } from 'types/common';
import useDataList from 'utils/hooks/useDataList';
import { useDebounce } from 'utils/hooks/useDebounce';

import { RINGCENTRAL_RECORDS_INITIAL_PAGE, RINGCENTRAL_RECORDS_PAGE_SIZE } from './constants';
import { RingCentralCommunicationType, RingCentralRecordsFiltersType } from './types';

const getSelectedValues = (values?: string[]) => {
  return values?.length ? values : undefined;
};

export const useRingCentralRecordsPage = () => {
  const { currentUser } = useAppSelector((state) => state.auth);

  const { items, totalItemsCount, filters, setItems, setPage, setFilters } = useDataList({
    initialState: {
      filters: {
        page: RINGCENTRAL_RECORDS_INITIAL_PAGE,
        pageSize: RINGCENTRAL_RECORDS_PAGE_SIZE,
        search: '',
        source_type: [],
        direction: [],
        matching_status: [],
        media_type: [],
      },
    },
  });

  const ringCentralFilters = filters as RingCentralRecordsFiltersType;
  const debouncedSearch = useDebounce(ringCentralFilters.search, 300);

  const { data, isLoading, isFetching } = useQuery<PaginatedResponseType<RingCentralCommunicationType>>({
    queryKey: [
      'ringcentral-communications',
      ringCentralFilters.page,
      ringCentralFilters.pageSize,
      debouncedSearch,
      ringCentralFilters.source_type,
      ringCentralFilters.direction,
      ringCentralFilters.matching_status,
      ringCentralFilters.media_type,
    ],
    queryFn: () =>
      getRingCentralCommunications({
        page: ringCentralFilters.page,
        page_size: ringCentralFilters.pageSize,
        search: debouncedSearch.trim() || undefined,
        source_type: getSelectedValues(ringCentralFilters.source_type),
        direction: getSelectedValues(ringCentralFilters.direction),
        matching_status: getSelectedValues(ringCentralFilters.matching_status),
        media_type: getSelectedValues(ringCentralFilters.media_type),
      }),
    enabled: canViewRingCentralRecords(currentUser),
    refetchInterval: 30000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      setItems(response);
    },
  });

  return {
    items: items ?? data?.results ?? [],
    loading: isLoading || isFetching,
    totalItemsCount: totalItemsCount || data?.count || 0,
    filters: ringCentralFilters,
    setPage,
    setFilters,
  };
};
